<script lang="ts">
	/**
	 * Prices, and the immutability rule **as the service states it** (Design §4.1).
	 *
	 * The rule is per *operation*, not per column, so this panel offers exactly two things:
	 *
	 * - **Add currency** on an existing price. It cannot reprice anyone — every live subscription
	 *   already bills in a currency that is present — so it is offered on live prices, which is
	 *   what makes "enable a new currency" a routine toggle rather than a catalog rebuild.
	 * - **New price**, for anything else, with the note that it replaces the current price for new
	 *   customers. Existing subscriptions keep what they were sold at.
	 *
	 * There is deliberately no "edit amount" control. The service refuses it; a field here would
	 * only be a promise the API breaks.
	 */

	import {
		Badge,
		Button,
		Input,
		Table,
		TableBody,
		TableBodyCell,
		TableBodyRow,
		TableHead,
		TableHeadCell,
		Toggle
	} from 'flowbite-svelte';
	import { createEventDispatcher } from 'svelte';
	import {
		ConfirmButton,
		CurrencyPicker,
		EmptyState,
		FormField,
		FormModal,
		Panel,
		SelectField
	} from '../../../components';
	import {
		addPriceCurrency,
		archivePrice,
		createPrice,
		issueReprice,
		previewReprice,
		replacePrice,
		updatePrice
	} from '../../../AppNetwork';
	import { toastError, toastSuccess } from '../../../stores/toast';
	import { BILLING_INTERVALS, formatCalendarDate, formatInterval, formatMoney, majorUnitsFrom, minorUnitsFrom } from '../../../Utilities';
	import type {
		BillingInterval,
		Price,
		Product,
		RepricePreview,
		RepriceResult,
		StorefrontCurrency
	} from '../../../AppTypes';

	export let product: Product | null = null;
	export let currencies: StorefrontCurrency[] = [];

	/**
	 * Rendered inside a dialog rather than as a section of the catalogue page.
	 *
	 * Only the chrome changes: a widget's border and shadow inside a modal draw a second box
	 * around a box. The panel is the same panel either way, so there is one copy of the
	 * immutability rules rather than one per surface.
	 */
	export let embedded: boolean = false;

	const dispatcher = createEventDispatcher();

	let isAddingPrice: boolean = false;

	/**
	 * The price being replaced, if any (§4.1).
	 *
	 * Replacing is the sanctioned way to change an amount — the old row keeps every value it was
	 * sold at and a new one supersedes it — so the form is the *same* form, prefilled, and the
	 * only difference is which endpoint it submits to.
	 */
	let replacingPrice: Price | null = null;

	/**
	 * Repricing **existing** subscribers (§9.8) — the deliberate exception to §4.1's immutability,
	 * and the only thing here that moves the price of a running contract.
	 *
	 * Two-step on purpose: the preview is loaded before the confirm button exists, because a
	 * reprice cannot be recalled once issued. The notice is the thing with legal effect, and it has
	 * already reached the customer by the time an admin realises they picked the wrong price.
	 */
	let repricingFrom: Price | null = null;
	let repriceTarget: string = '';
	let repriceReason: string = '';
	let repricePreview: RepricePreview | null = null;
	let repricePreviewing: boolean = false;
	let repriceError: string = '';
	let repriceResult: RepriceResult | null = null;

	/** Every other active price on this product — a reprice keeps the product and the period. */
	$: repriceTargets = (product?.prices ?? []).filter(
		candidate =>
			candidate.isActive &&
			repricingFrom !== null &&
			candidate.uuid !== repricingFrom.uuid &&
			candidate.interval === repricingFrom.interval &&
			candidate.intervalCount === repricingFrom.intervalCount
	);

	/** A price's amount in one currency, or 0 when it carries none there. */
	function fnAmountOf(price: Price, code: string): number {
		return price.amounts.find(entry => entry.currency === code)?.amountMinor ?? 0;
	}

	/**
	 * Says what to do, not only that it cannot be done.
	 *
	 * "There is no price to move them to" is what an admin sees when they started from the wrong
	 * row — most often the freshly created price rather than the archived one their subscribers are
	 * actually on — so the message names the fix.
	 */
	$: noRepriceTargetHelp =
		repricingFrom === null
			? ''
			: `A reprice keeps the same product and the same billing period. Use Update price to create the new one (that archives this price and activates its successor), then reprice from the archived row, which is where your existing subscribers are.`;

	function fnStartReprice(price: Price): void {
		repricingFrom = price;
		repriceTarget = '';
		repriceReason = '';
		repricePreview = null;
		repriceError = '';
		repriceResult = null;
	}

	/**
	 * A preview is only true for the price it was asked about, so changing the target discards it.
	 * Confirming against a stale preview would be issuing notices to a headcount nobody checked.
	 */
	function fnRepriceTargetChanged(): void {
		repricePreview = null;
		repriceError = '';
	}

	async function fnPreviewReprice(): Promise<void> {
		if (repricingFrom === null || repriceTarget === '') return;

		repricePreview = null;
		repriceError = '';
		repricePreviewing = true;
		try {
			repricePreview = await previewReprice(repricingFrom.uuid, repriceTarget);
		} catch (error) {
			repriceError = (error as Error).message;
		} finally {
			repricePreviewing = false;
		}
	}

	async function fnIssueReprice(): Promise<void> {
		if (repricingFrom === null || repricePreview === null) return;

		saving = true;
		try {
			repriceResult = await issueReprice(repricingFrom.uuid, repriceTarget, repriceReason.trim());
			toastSuccess(
				`${repriceResult.notified} ${repriceResult.notified === 1 ? 'customer has' : 'customers have'} been notified.`,
				'Nothing changes until their notice period ends.'
			);
			repricingFrom = null;
			dispatcher('changed');
		} catch (error) {
			toastError('The reprice was not issued.', (error as Error).message);
		} finally {
			saving = false;
		}
	}
	let addingCurrencyTo: string = '';
	let saving: boolean = false;
	let busyUUID: string = '';

	/*
	 * §4.1 — the same column, two vocabularies. `isSeatBased` says the amount is per unit and the
	 * buyer picks how many; on a one-time product those units are not seats, and a form that calls
	 * them seats is how a repeatable product ends up sold "per seat" to nobody's users.
	 */
	$: isUnitPriced = product?.type === 'one_time';

	let priceDraft = { interval: 'month', intervalCount: 1, baseCurrency: '', taxBehavior: 'exclusive', trialDays: 0, isDefault: true, isSeatBased: false, seatMin: 1, seatMax: '' as string, amounts: {} as Record<string, string> };
	/** The amount is the decimal an admin types — "19.00" — not minor units. */
	let currencyDraft = { currency: '', amount: '' };

	/**
	 * The draft price being edited in place, if any (§4.1).
	 *
	 * A price nothing has been sold at and no provider has seen protects nobody, so it is edited
	 * rather than replaced. Refusing would make correcting a typo mean archiving a row and
	 * rebuilding it, which is how a catalogue fills with dead prices. The server decides which
	 * prices these are — `isDraft` on the DTO — because a rule duplicated here would drift into a
	 * button that opens a form the API refuses.
	 */
	let editingDraft: Price | null = null;

	/**
	 * Which product every form on this screen is about.
	 *
	 * The panel opens over the catalogue rather than beside it, so the row that was clicked is
	 * behind a backdrop by the time the form is on screen — and "New price" over a dimmed table
	 * names no plan at all. A price is the one thing here that is meaningless without its product:
	 * a catalogue holds several tiers whose forms are otherwise identical, and the cost of getting
	 * it wrong is a live amount attached to the wrong plan.
	 */
	$: productName = product?.name ?? 'this product';
	$: priceFormTitle =
		editingDraft !== null
			? `Edit price for ${productName}`
			: replacingPrice === null
				? `New price for ${productName}`
				: `Update price for ${productName}`;

	$: prices = product?.prices ?? [];
	$: addingTo = prices.find(price => price.uuid === addingCurrencyTo) ?? null;
	/** Only currencies this price does not already carry: overwriting one is the refused edit. */
	$: addableCurrencies =
		addingTo === null
			? []
			: currencies.filter(currency => !addingTo.amounts.some(amount => amount.currency === currency.code));

	function fnRowFor(code: string): StorefrontCurrency | undefined {
		return currencies.find(currency => currency.code === code);
	}

	/**
	 * The decimal places a currency uses, for the amount fields.
	 *
	 * Two is **not** the fallback. JPY has none, and a form that assumes two turns ¥1,200 into
	 * ¥12 on the way in and back out again — silently, because both figures look like money. An
	 * unknown currency gets zero, so the value an admin types is the value stored.
	 */
	function fnDigitsOf(code: string): number {
		return fnRowFor(code)?.minorUnitDigits ?? 0;
	}

	/** What the admin typed, as minor units, or null when it is not a money amount. */
	function fnMinorFrom(code: string, value: string): number | null {
		return minorUnitsFrom(value, fnDigitsOf(code));
	}

	function fnFormat(code: string, amountMinor: number): string {
		const row = fnRowFor(code);
		// No row means no `minorUnitDigits`, and guessing two is the zero-decimal bug. Show the
		// raw minor units and say so rather than inventing a decimal point.
		return row === undefined ? `${amountMinor} (minor units, ${code.toUpperCase()})` : formatMoney(amountMinor, row);
	}

	function fnResetPriceDraft(): void {
		const preferred = currencies.find(currency => currency.isDefault)?.code ?? currencies[0]?.code ?? '';
		priceDraft = {
			interval: product?.type === 'one_time' ? '' : 'month',
			intervalCount: 1,
			baseCurrency: preferred,
			taxBehavior: 'exclusive',
			trialDays: 0,
			isSeatBased: false,
			seatMin: 1,
			seatMax: '',
			isDefault: true,
			amounts: {}
		};
	}

	async function fnCreatePrice(): Promise<void> {
		if (product === null) return;

		/*
		 * A blank currency is left out — that is how a product is made unavailable in it. An
		 * *unparseable* one is left out too rather than sent as zero: a typo read as zero is a
		 * free plan, and nothing about the resulting row looks wrong.
		 */
		const amounts = Object.entries(priceDraft.amounts)
			.map(([currency, value]) => ({ currency, amountMinor: fnMinorFrom(currency, `${value}`) }))
			.filter((entry): entry is { currency: string; amountMinor: number } => entry.amountMinor !== null);

		saving = true;
		try {
			const input = {
				productUUID: product.uuid,
				interval: priceDraft.interval.length === 0 ? null : (priceDraft.interval as BillingInterval),
				intervalCount: Number(priceDraft.intervalCount),
				baseCurrency: priceDraft.baseCurrency,
				taxBehavior: priceDraft.taxBehavior as 'inclusive' | 'exclusive',
				trialDays: Number(priceDraft.trialDays),
				isDefault: priceDraft.isDefault,
				// Sent only for a per-seat price. The server refuses seat terms on a flat one by
				// name rather than ignoring them, so passing them always would turn every flat
				// price into a validation error.
				isSeatBased: priceDraft.isSeatBased,
				...(priceDraft.isSeatBased
					? {
						seatMin: Number(priceDraft.seatMin),
						// An empty ceiling means no ceiling, which is null — not zero, which would
						// be a plan nobody can hold.
						seatMax: priceDraft.seatMax === '' ? null : Number(priceDraft.seatMax)
					}
					: {}),
				amounts
			};

			/*
			 * One body, three endpoints. Editing a draft rewrites the row; replacing archives it
			 * and writes a successor; creating writes a new one. The distinction is which of those
			 * three the admin asked for, and the server re-checks draftness rather than trusting
			 * this — the flag it sent could be a page load old.
			 */
			if (editingDraft !== null) {
				await updatePrice(editingDraft.uuid, input);
				toastSuccess('Price updated.', 'Nothing had been sold at it, so it was edited rather than replaced.');
			} else if (replacingPrice !== null) {
				await replacePrice(replacingPrice.uuid, input);
				toastSuccess('Price replaced.', 'The old price is archived; existing subscriptions keep billing at it.');
			} else {
				await createPrice(input);
				toastSuccess('Price created.');
			}

			isAddingPrice = false;
			replacingPrice = null;
			editingDraft = null;
			fnResetPriceDraft();
			dispatcher('changed');
		} catch (error) {
			toastError(
				editingDraft !== null
					? 'The price was not updated.'
					: replacingPrice === null
						? 'The price was not created.'
						: 'The price was not replaced.',
				(error as Error).message
			);
		} finally {
			saving = false;
		}
	}

	async function fnAddCurrency(): Promise<void> {
		saving = true;
		try {
			await addPriceCurrency(addingCurrencyTo, {
				currency: currencyDraft.currency,
				amountMinor: fnMinorFrom(currencyDraft.currency, currencyDraft.amount) ?? 0
			});
			toastSuccess(`${currencyDraft.currency.toUpperCase()} added to the price.`);
			addingCurrencyTo = '';
			currencyDraft = { currency: '', amount: '' };
			dispatcher('changed');
		} catch (error) {
			toastError('The currency was not added.', (error as Error).message);
		} finally {
			saving = false;
		}
	}

	/**
	 * Archived rows recede, and only their **data** does.
	 *
	 * A product accumulates archived prices forever — §4.1 makes an amount change an
	 * archive-and-recreate, so every repricing leaves one behind — and undimmed they read as live
	 * prices with a badge, on the one screen where mistaking the two means repricing a customer.
	 *
	 * The State and actions columns are deliberately left at full strength. The **Archived** badge
	 * is what explains the dimming, so dimming it too would hide the label that makes the row
	 * legible; and **Reprice** is offered on archived rows *precisely* because that is where the
	 * existing subscribers are, which makes it the one action on this screen that a dimmed row is
	 * most likely to need.
	 */
	function fnDim(price: Price): string {
		return price.isActive ? '' : 'opacity-50';
	}

	async function fnArchive(price: Price): Promise<void> {
		busyUUID = price.uuid;
		try {
			await archivePrice(price.uuid);
			toastSuccess('Price archived. Existing subscriptions keep billing at it.');
			dispatcher('changed');
		} catch (error) {
			toastError('The price was not archived.', (error as Error).message);
		} finally {
			busyUUID = '';
		}
	}

	async function fnMakeDefault(price: Price): Promise<void> {
		busyUUID = price.uuid;
		try {
			await updatePrice(price.uuid, { isDefault: true });
			toastSuccess('Default price updated.');
			dispatcher('changed');
		} catch (error) {
			toastError('The price was not changed.', (error as Error).message);
		} finally {
			busyUUID = '';
		}
	}

	function fnStartAddPrice(): void {
		replacingPrice = null;
		editingDraft = null;
		fnResetPriceDraft();
		isAddingPrice = true;
	}

	/** The price's own values, as the decimals the form takes. */
	function fnDraftFrom(price: Price) {
		return {
			interval: price.interval ?? '',
			intervalCount: price.intervalCount,
			baseCurrency: price.baseCurrency,
			taxBehavior: price.taxBehavior,
			trialDays: price.trialDays,
			isSeatBased: price.isSeatBased,
			seatMin: price.seatMin,
			seatMax: price.seatMax === null ? '' : String(price.seatMax),
			isDefault: price.isDefault,
			// Every currency the price is sold in, so neither a replacement nor a draft edit
			// silently drops one and makes the product unavailable in it.
			amounts: Object.fromEntries(
				price.amounts.map(amount => [
					amount.currency,
					majorUnitsFrom(amount.amountMinor, fnDigitsOf(amount.currency))
				])
			) as Record<string, string>
		};
	}

	/** Editing a price nothing depends on yet — the row is rewritten, not superseded. */
	function fnStartEditDraft(price: Price): void {
		replacingPrice = null;
		editingDraft = price;
		priceDraft = fnDraftFrom(price);
		isAddingPrice = true;
	}

	/**
	 * Opens the form on an existing price's own values.
	 *
	 * Prefilled from what is there so the admin edits the one thing they came to change; a blank
	 * form for a "change the price" action invites a transcription mistake in every other field,
	 * and those fields are the terms customers are sold on.
	 */
	function fnStartReplacePrice(price: Price): void {
		editingDraft = null;
		replacingPrice = price;
		priceDraft = fnDraftFrom(price);
		isAddingPrice = true;
	}

	function fnStartAddCurrency(price: Price): void {
		addingCurrencyTo = price.uuid;
		currencyDraft = { currency: '', amount: '' };
	}
</script>

<!--
	Embedded, the dialog's own titlebar already names the product and stays put while the panel
	scrolls, so repeating it in the panel heading would be one fact rendered twice on one screen.
-->
<Panel
	title={product === null || embedded ? 'Prices' : `Prices for ${product.name}`}
	faIcon="tags"
	borderless={embedded}
	compact={embedded}
>
	<svelte:fragment slot="titleRight">
		{#if product !== null}
			<Button size="sm" color="primary" on:click={fnStartAddPrice}>
				<i class="fa-solid fa-plus mr-2" />New price
			</Button>
		{/if}
	</svelte:fragment>

	{#if product === null}
		<EmptyState
			title="Pick a product."
			description="Choose Prices on a product above to see and author its prices."
		/>
	{:else if prices.length === 0}
		<EmptyState
			title="This product has no price."
			description="A product with no price is unbuyable, and the store stays closed until one active product has an active price."
		>
			<Button color="primary" size="sm" on:click={fnStartAddPrice}>Add the first price</Button>
		</EmptyState>
	{:else}
		<Table divClass="relative overflow-x-auto">
			<TableHead>
				<TableHeadCell>Billing</TableHeadCell>
				<TableHeadCell>Amounts</TableHeadCell>
				<TableHeadCell>Trial</TableHeadCell>
				<TableHeadCell>Tax</TableHeadCell>
				<TableHeadCell>State</TableHeadCell>
				<TableHeadCell />
			</TableHead>
			<TableBody>
				{#each prices as price (price.uuid)}
					<TableBodyRow>
						<TableBodyCell class={`dataTable ${fnDim(price)}`}>
							{price.interval === null ? 'One-time' : `Every ${formatInterval(price.interval, price.intervalCount).slice(1)}`}
						</TableBodyCell>
						<TableBodyCell class={`dataTable ${fnDim(price)}`}>
							<div class="flex flex-col gap-0.5">
								{#each price.amounts as amount (amount.currency)}
									<span class="text-sm">
										<span class="font-mono text-xs text-default mr-1">{amount.currency.toUpperCase()}</span>
										{fnFormat(amount.currency, amount.amountMinor)}
									</span>
								{/each}
								{#if price.amounts.length === 0}
									<span class="text-default text-sm">No amount</span>
								{/if}
							</div>
						</TableBodyCell>
						<TableBodyCell class={`dataTable ${fnDim(price)}`}>
							{price.trialDays > 0 ? `${price.trialDays} days` : '—'}
						</TableBodyCell>
						<TableBodyCell class={`dataTable ${fnDim(price)}`}>{price.taxBehavior}</TableBodyCell>
						<TableBodyCell class="dataTable">
							<div class="flex flex-wrap gap-1">
								{#if price.isDefault}<Badge color="green">Default</Badge>{/if}
								{#if !price.isActive}<Badge color="dark">Archived</Badge>{/if}
								<!-- Says why Edit is or is not on offer, rather than leaving an admin to
								     work out why one row has it and its neighbour does not. -->
								{#if price.isDraft === true}
									<Badge color="yellow" title="Nothing has been sold at it yet, so it can still be edited">Draft</Badge>
								{:else if price.notDraftReason}
									<Badge color="blue" title={`Cannot be edited: ${price.notDraftReason}`}>Live</Badge>
								{/if}
							</div>
						</TableBodyCell>
						<TableBodyCell class="dataTable">
							<div class="flex flex-wrap gap-2">
								<!-- The permitted edit. Offered on a live price precisely because it
								     cannot reprice anyone. -->
								<Button
									size="xs"
									color="alternative"
									disabled={busyUUID === price.uuid || !price.isActive}
									on:click={() => fnStartAddCurrency(price)}
								>
									Add currency
								</Button>
								<!--
									Changing an amount is archive-and-recreate (§4.1), so this opens the
									same form prefilled rather than an edit form — there is nothing to
									edit, and calling it "Edit" would suggest existing subscriptions
									move with it.
								-->
								{#if price.isActive && price.isDraft === true}
									<!--
										§4.1's immutability protects what somebody was sold. A price
										nothing has been sold at and no provider has seen protects
										nobody, so it is edited rather than superseded — otherwise
										fixing a typo in an unpublished draft means archiving a row
										and rebuilding it, which is how a catalogue fills with dead
										prices. The server decides which prices these are.
									-->
									<Button
										size="xs"
										color="alternative"
										disabled={busyUUID === price.uuid}
										on:click={() => fnStartEditDraft(price)}
									>
										<i class="fa-solid fa-pen mr-1" />Edit
									</Button>
								{:else if price.isActive}
									<Button
										size="xs"
										color="alternative"
										disabled={busyUUID === price.uuid}
										on:click={() => fnStartReplacePrice(price)}
									>
										Update price
									</Button>
								{/if}
								<!--
									Offered on **archived** prices too, and that is the common case rather
									than an edge one: "Update price" archives the old price and activates
									its successor, so a moment later every existing subscriber is sitting
									on an archived row. That is precisely who a reprice is for.
									
									Kept visually separate from "Update price" because one moves the
									price of contracts people are already paying and the other only ever
									affects new customers — confusing the two is the expensive mistake
									available on this screen.
								-->
								<Button
									size="xs"
									color="alternative"
									disabled={busyUUID === price.uuid}
									on:click={() => fnStartReprice(price)}
								>
									Reprice subscribers
								</Button>
								{#if price.isActive && !price.isDefault}
									<Button size="xs" color="alternative" disabled={busyUUID === price.uuid} on:click={() => fnMakeDefault(price)}>
										Make default
									</Button>
								{/if}
								{#if price.isActive}
									<ConfirmButton
										size="xs"
										color="alternative"
										disabled={busyUUID === price.uuid}
										confirmText="Archive this price? Nobody new can buy it. Existing subscriptions keep billing at it, because the alternative is silently repricing a customer."
										on:confirm={() => fnArchive(price)}
									>
										Archive
									</ConfirmButton>
								{/if}
							</div>
						</TableBodyCell>
					</TableBodyRow>
				{/each}
			</TableBody>
		</Table>

		<p class="text-xs text-default mt-3">
			<i class="fa-solid fa-lock mr-1" />An existing amount, interval or tax behaviour cannot be edited:
			existing subscriptions keep what they were sold at. Create a new price and archive the old one.
			Adding a currency to a live price is allowed, because it cannot reprice anyone.
		</p>
	{/if}

	<!--
		Adding a currency is an edit of the price in the row above, so it belongs in a modal rather
		than a panel that pushes the table it refers to off the screen.
	-->
	<FormModal
		open={addingTo !== null}
		title={`Add a currency for ${productName}`}
		faIcon="coins"
		submitLabel="Add currency"
		busy={saving}
		canSubmit={currencyDraft.currency.length > 0 && addableCurrencies.length > 0}
		description="Allowed on a live price, because adding a currency cannot reprice anyone."
		on:submit={fnAddCurrency}
		on:cancel={() => (addingCurrencyTo = '')}
	>
		{#if addableCurrencies.length === 0}
			<EmptyState compact title="This price already carries every enabled currency." />
		{:else}
					<div class="grid md:grid-cols-2 gap-4">
						<FormField id="add-currency" label="Currency" required>
							<CurrencyPicker
								id="add-currency"
								label="Currency to add"
								currencies={addableCurrencies}
								bind:value={currencyDraft.currency}
								disabled={saving}
							/>
						</FormField>
						<FormField
							id="add-amount"
							label="Amount"
							required
							description={currencyDraft.currency.length > 0
								? `As the customer is charged: ${fnFormat(currencyDraft.currency, fnMinorFrom(currencyDraft.currency, currencyDraft.amount) ?? 0)}`
								: 'Pick a currency first: how many decimal places this takes depends on it.'}
						>
							<!-- Text, not `type="number"`: a number input rejects the comma half of Europe
							     types and its spinner steps by whole units, which on a decimal field
							     means stepping a euro at a time. -->
							<Input
								id="add-amount"
								type="text"
								inputmode="decimal"
								placeholder={majorUnitsFrom(0, fnDigitsOf(currencyDraft.currency))}
								bind:value={currencyDraft.amount}
								disabled={saving}
							/>
						</FormField>
					</div>
		{/if}
	</FormModal>

	<!--
		Repricing existing subscribers (§9.8).
		
		Deliberately heavier than every other form here: it is the only action on this screen that
		moves the price of a contract someone is already paying, it cannot be recalled once issued,
		and the confirm button does not exist until a preview has been loaded.
	-->
	{#if repricingFrom !== null}
		<FormModal
			open={true}
			title={`Reprice subscribers of ${productName}`}
			faIcon="scale-balanced"
			submitLabel={repricePreview === null
				? 'Check who this affects'
				: `Notify ${repricePreview.affected} ${repricePreview.affected === 1 ? 'subscriber' : 'subscribers'}`}
			size="lg"
			busy={saving || repricePreviewing}
			canSubmit={repricePreview === null
				? repriceTarget !== ''
				: repriceReason.trim().length > 0 && repricePreview.affected > 0}
			description="Everyone on this price is given advance notice, keeps their current price until their next renewal after it ends, and can cancel before it takes effect. Nobody is charged today."
			on:submit={() => (repricePreview === null ? void fnPreviewReprice() : void fnIssueReprice())}
			on:cancel={() => (repricingFrom = null)}
		>
			{#if repriceTargets.length === 0}
				<!-- Not `compact`: that hides the description, and the description is the whole point. -->
				<EmptyState
					title="There is no active price to move them to."
					description={noRepriceTargetHelp}
				/>
			{:else}
				<FormField
					id="reprice-target"
					label="Move them onto"
					required
					description="Same product, same billing period. Moving tier is a subscription change, not a reprice."
				>
					<SelectField
						id="reprice-target"
						bind:value={repriceTarget}
						disabled={saving}
						on:change={fnRepriceTargetChanged}
						items={repriceTargets.map(candidate => ({
							value: candidate.uuid,
							name: `${fnFormat(candidate.baseCurrency, fnAmountOf(candidate, candidate.baseCurrency))}${formatInterval(candidate.interval, candidate.intervalCount)}`
						}))}
						placeholder="Choose the new price"
					/>
				</FormField>

				{#if repriceError.length > 0}
					<p class="text-sm text-red-600 dark:text-red-400">{repriceError}</p>
				{/if}

				{#if repricePreview !== null}
					<div class="widget bg-depth border-widget text-sm" role="status">
						<p class="card-label mb-2">
							<i class="fa-solid fa-users text-[11px] mr-1" />What this does
						</p>
						{#if repricePreview.affected === 0}
							<p>Nobody is on this price, so there is nothing to reprice.</p>
						{:else}
							<p>
								<strong>{repricePreview.affected}</strong>
								{repricePreview.affected === 1 ? 'subscriber' : 'subscribers'} would be notified, with the change taking effect no earlier than
								<!--
									A rise is the regulated case, so it is called out rather than folded
									into a single headcount.
								-->
								<strong>{repricePreview.increases}</strong> would pay more,
								{repricePreview.decreases} would pay less, and
								{repricePreview.unchanged} would pay the same.
							</p>
							<p class="mt-2">
								Each keeps their current price until their first renewal on or after
								<strong>{formatCalendarDate(repricePreview.earliestEffectiveAt)}</strong>
								({repricePreview.noticeDays} days from now). They can cancel before then.
							</p>
						{/if}
					</div>

					<FormField
						id="reprice-reason"
						label="Reason"
						required
						description="Shown to every customer in the notice, and kept in the audit trail. A price rise with no stated reason is the one regulators treat least kindly."
					>
						<Input
							id="reprice-reason"
							bind:value={repriceReason}
							placeholder="Our infrastructure costs have risen since this subscription was first priced."
							disabled={saving}
						/>
					</FormField>

					<!--
						Stated immediately above the button that does it. Once the notices are out they
						cannot be recalled — the notice is the thing with legal effect.
					-->
					<p class="text-xs text-red-600 dark:text-red-400">
						<i class="fa-solid fa-triangle-exclamation mr-1" />Issuing sends the notices
						straight away and cannot be undone.
					</p>
				{/if}
			{/if}
		</FormModal>
	{/if}

	<!--
		The price form, in a modal. Inline it appeared *below* the table, so the admin lost sight of
		the row they were replacing at the moment they most needed to check it.
	-->
	{#if product !== null}
		<FormModal
			bind:open={isAddingPrice}
			title={priceFormTitle}
			faIcon={editingDraft !== null || replacingPrice !== null ? 'pen' : 'plus'}
			submitLabel={editingDraft !== null ? 'Save price' : replacingPrice === null ? 'Create price' : 'Replace price'}
			size="lg"
			busy={saving}
			canSubmit={priceDraft.baseCurrency.length > 0}
			description={editingDraft !== null
				? 'Nothing has been sold at this price and no provider has seen it, so it is edited in place. Once either is true it can only be replaced.'
				: replacingPrice === null
					? 'This applies to new customers. Existing subscriptions keep billing at the price they were sold at.'
					: 'Submitting archives the current price and activates this one in its place, for new customers. Existing subscriptions keep billing at the price they were sold at, and nobody is repriced.'}
			on:submit={fnCreatePrice}
			on:cancel={() => {
				replacingPrice = null;
				editingDraft = null;
			}}
		>
			<div class="grid md:grid-cols-3 gap-4">
				{#if product.type === 'subscription'}
					<!-- Daily is here for testing: it is the only interval that produces a second
					     invoice soon enough to watch renewal, dunning and expiry actually happen. -->
					<FormField id="price-interval" label="Interval" required>
						<SelectField
							id="price-interval"
							bind:value={priceDraft.interval}
							items={BILLING_INTERVALS}
							disabled={saving}
						/>
					</FormField>
					<FormField id="price-count" label="Interval count" description="Bill every N intervals.">
						<Input id="price-count" type="number" min="1" step="1" bind:value={priceDraft.intervalCount} disabled={saving} />
					</FormField>
					<FormField
						id="price-trial"
						label="Trial days"
						description="One trial per customer per family (§9.2)."
					>
						<Input id="price-trial" type="number" min="0" step="1" bind:value={priceDraft.trialDays} disabled={saving} />
					</FormField>
				{/if}

				<!--
					Seat terms (§9.7). A family may hold flat and per-seat tiers side by side,
					so this is a property of the price and never of the product.
				-->
				<FormField
					id="price-seat-based"
					label={isUnitPriced ? 'Sold by the unit' : 'Sold per seat'}
					description={isUnitPriced
						? 'The amounts below become the price of one unit, and the buyer chooses how many. One column in the database, two words for it, because a one-time product is not sold seats.'
						: 'The amounts below become the price of one seat, billed as a multiple of the seat count.'}
				>
					<Toggle id="price-seat-based" bind:checked={priceDraft.isSeatBased} disabled={saving} />
				</FormField>

				{#if priceDraft.isSeatBased}
					<FormField
						id="price-seat-min"
						label={isUnitPriced ? 'Minimum quantity' : 'Minimum seats'}
						description={isUnitPriced
							? 'The fewest units one purchase can be for.'
							: 'The fewest seats this subscription can be bought or reduced to.'}
					>
						<Input id="price-seat-min" type="number" min="1" step="1" bind:value={priceDraft.seatMin} disabled={saving} />
					</FormField>

					<FormField
						id="price-seat-max"
						label={isUnitPriced ? 'Maximum quantity' : 'Maximum seats'}
						description="Leave empty for no ceiling."
					>
						<Input id="price-seat-max" type="number" min="1" step="1" bind:value={priceDraft.seatMax} disabled={saving} />
					</FormField>
				{/if}

				<!--
					Which side of tax the amounts below are on. It reinterprets every one of them —
					the same 1000 is what the customer pays under `inclusive` and what the store
					receives under `exclusive` — which is why §4.1 refuses to change it on a live
					price, and why it has to be right at creation.
				-->
				<FormField
					id="price-tax"
					label="Tax behaviour"
					required
					description="Exclusive adds tax on top at checkout. Inclusive means the amount already contains it, so the customer pays exactly what is shown. EU consumer pricing is conventionally inclusive."
				>
					<SelectField
						id="price-tax"
						bind:value={priceDraft.taxBehavior}
						disabled={saving}
						items={[
							{ value: 'exclusive', name: 'Exclusive (tax added at checkout)', description: 'The amount is before tax; the buyer pays the amount plus their tax.' },
							{ value: 'inclusive', name: 'Inclusive (tax already in the amount)', description: 'The amount already contains tax; the store keeps less where the rate is higher.' }
						]}
					/>
				</FormField>

				<FormField
					id="price-base"
					label="Base currency"
					required
					description="Must be one of the amounts below."
				>
					<CurrencyPicker
						id="price-base"
						label="Base currency"
						{currencies}
						bind:value={priceDraft.baseCurrency}
						disabled={saving}
					/>
				</FormField>
			</div>

			<div>
				<div class="card-label mb-2">
					<i class="fa-solid fa-coins text-[11px] mr-1" />Amounts
				</div>
				<div class="grid md:grid-cols-3 gap-4">
					{#each currencies as currency (currency.code)}
						<FormField
							id={`price-amount-${currency.code}`}
							label={`${currency.code.toUpperCase()} (${currency.name})`}
							description={`As charged: ${fnFormat(currency.code, fnMinorFrom(currency.code, `${priceDraft.amounts[currency.code] ?? ''}`) ?? 0)}`}
						>
							<Input
								id={`price-amount-${currency.code}`}
								type="text"
								inputmode="decimal"
								placeholder={majorUnitsFrom(0, currency.minorUnitDigits)}
								bind:value={priceDraft.amounts[currency.code]}
								disabled={saving}
							/>
						</FormField>
					{/each}
				</div>
				<p class="text-xs text-default mt-2">
					Leave a currency blank to leave the product unavailable in it, and the storefront says so
					rather than quoting the base currency.
				</p>
			</div>

			<div class="flex items-center gap-3">
				<Toggle id="price-is-default" bind:checked={priceDraft.isDefault} disabled={saving} />
				<label for="price-is-default" class="text-sm">
					Make this the default price for its interval
				</label>
			</div>
		</FormModal>
	{/if}
</Panel>
